import type { CameraName, ControlAck } from '@digger/shared';

/**
 * Latency instrumentation — measured, not asserted (DECISIONS.md §6).
 *
 * Control RTT: every frame carries the client's send time; the agent echoes
 * it in the ack. rtt = now - tClient, EMA-smoothed. This traverses the real
 * control path end to end (keyboard tick → DataChannel → agent apply → ack).
 */
export class RttTracker {
  private ema: number | undefined;
  private lastAckAt: number | undefined;
  lastAck: ControlAck | undefined;

  onAck(ack: ControlAck, now = performance.now()): void {
    const rtt = Date.now() - ack.tClient;
    if (rtt >= 0 && rtt < 10_000) {
      this.ema = this.ema === undefined ? rtt : 0.8 * this.ema + 0.2 * rtt;
    }
    this.lastAck = ack;
    this.lastAckAt = now;
  }

  get rttMs(): number | undefined {
    return this.ema;
  }

  /** How long since the agent last confirmed our control input. */
  ackAgeMs(now = performance.now()): number | undefined {
    return this.lastAckAt === undefined ? undefined : now - this.lastAckAt;
  }

  reset(): void {
    this.ema = undefined;
    this.lastAckAt = undefined;
    this.lastAck = undefined;
  }
}

export interface VideoStats {
  fps?: number;
  jitterBufferMs?: number;
  decodeMs?: number;
  framesDecoded?: number;
}

export interface LatencyReport {
  networkRttMs?: number;
  perCamera: Partial<Record<CameraName, VideoStats>>;
  /** captureEncode + rtt/2 + jitterBuffer + decode + one display refresh. */
  glassToGlassEstimateMs?: (camera: CameraName) => number | undefined;
}

interface InboundSnapshot {
  framesDecoded: number;
  totalDecodeTime: number;
  jitterBufferDelay: number;
  jitterBufferEmittedCount: number;
  at: number;
}

const DISPLAY_REFRESH_MS = 8; // ~half a 60 Hz frame, average queueing to glass

/**
 * Polls RTCPeerConnection.getStats() and decomposes a glass-to-glass
 * estimate per camera. The capture+encode constant is config (calibrated
 * once with the millisecond-clock method — see README).
 */
export class VideoStatsPoller {
  private prev = new Map<string, InboundSnapshot>();

  constructor(
    private readonly captureEncodeMs: number,
    private readonly midToCamera: () => Partial<Record<string, CameraName>>,
  ) {}

  async poll(pc: RTCPeerConnection): Promise<LatencyReport> {
    const stats = await pc.getStats();
    const perCamera: Partial<Record<CameraName, VideoStats>> = {};
    let networkRttMs: number | undefined;

    stats.forEach((report) => {
      if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
        if (typeof report.currentRoundTripTime === 'number') {
          networkRttMs = report.currentRoundTripTime * 1000;
        }
      }
      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        const camera = this.midToCamera()[report.mid ?? ''];
        if (!camera) return;
        const snap: InboundSnapshot = {
          framesDecoded: report.framesDecoded ?? 0,
          totalDecodeTime: report.totalDecodeTime ?? 0,
          jitterBufferDelay: report.jitterBufferDelay ?? 0,
          jitterBufferEmittedCount: report.jitterBufferEmittedCount ?? 0,
          at: performance.now(),
        };
        const prev = this.prev.get(report.id);
        this.prev.set(report.id, snap);
        if (!prev) return;

        const frames = snap.framesDecoded - prev.framesDecoded;
        const emitted = snap.jitterBufferEmittedCount - prev.jitterBufferEmittedCount;
        const elapsed = (snap.at - prev.at) / 1000;
        perCamera[camera] = {
          fps: elapsed > 0 ? frames / elapsed : undefined,
          decodeMs:
            frames > 0
              ? ((snap.totalDecodeTime - prev.totalDecodeTime) / frames) * 1000
              : undefined,
          jitterBufferMs:
            emitted > 0
              ? ((snap.jitterBufferDelay - prev.jitterBufferDelay) / emitted) * 1000
              : undefined,
          framesDecoded: snap.framesDecoded,
        };
      }
    });

    return {
      networkRttMs,
      perCamera,
      glassToGlassEstimateMs: (camera) => {
        const v = perCamera[camera];
        if (!v || networkRttMs === undefined) return undefined;
        if (v.jitterBufferMs === undefined || v.decodeMs === undefined) return undefined;
        return (
          this.captureEncodeMs +
          networkRttMs / 2 +
          v.jitterBufferMs +
          v.decodeMs +
          DISPLAY_REFRESH_MS
        );
      },
    };
  }

  reset(): void {
    this.prev.clear();
  }
}
